import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import ContentLayout from '../core/ContentLayout';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { ErrorMessage, Formik } from 'formik';
import { getPenaltyValidationSchema, penaltyPayValidationSchema } from '../../blocks/validation/validationSchema';
import { clearPenaltyAction, getPenaltyActions, payPenaltyActions } from '../../store/penalty/actions';
import GetPenaltyRequest from '../../models/penalty/GetPenaltyRequest';
import PayPenaltyRequest from '../../models/penalty/PayPenaltyRequest';
import I18n from '../../blocks/localization';
import { ApplicationState } from '../../store';
import NumberUtility from '../../blocks/utils/number-utility';
import CurrencyUtility from '../../blocks/utils/currency-utility/CurrencyUtility';
import DateUtility from '../../blocks/utils/date-utility';
import PrometModal from '../shared/Modal';
import TermsOfUseContent from './TermsOfUseContent';
import HorizontalDivider from '../shared/HorizontalDivider';
import { useLocation } from 'react-router';

type Props = typeof mapDispatchToProps;

const PenaltyPay = (props: Props) => {
    const location = useLocation();
    const queryReferenceNumber = new URLSearchParams(location.search).get('penalty-reference-number') ?? '';

    const [initialGetPenaltyRequest] = useState<GetPenaltyRequest>({ referenceNumber: queryReferenceNumber });
    const [initialPayPenaltyRequest, setInitialPayPenaltyRequest] = useState<PayPenaltyRequest>({ penaltyNumber: '', email: '', areTermsOfUseAccepted: false });
    const [isTermsOfUseModalOpen, setIsTermsOfUseModalOpen] = useState(false);

    const penalty = useSelector((state: ApplicationState) => state.penalty.penalty);
    const primaryCurrency = CurrencyUtility.getPrimary();

    const getPenaltyNumber = () => penalty?.referenceNumber ?? initialPayPenaltyRequest.penaltyNumber;
    const getAmount = () => penalty?.price;
    const getIssuedAt = () => penalty?.dateTimeCreated;
    const getStopName = () => penalty?.stopName;
    const getRouteName = () => penalty?.routeName;

    const termsOfUseModalClose = () => setIsTermsOfUseModalOpen(false);
    const termsOfUseModalOpen = () => setIsTermsOfUseModalOpen(true);

    useEffect(() => {
        if (queryReferenceNumber) {
            props.getPenalty({ referenceNumber: queryReferenceNumber })
        }

        return () => {
            props.clearPenalty();
        };
    }, []);

    const handleGetPenaltyFormSubmit = (values: GetPenaltyRequest) => {
        setInitialPayPenaltyRequest({
            penaltyNumber: values.referenceNumber,
            email: '',
            areTermsOfUseAccepted: false
        });
        props.getPenalty(values);
    }

    const handlePayPenaltyFormSubmit = (values: PayPenaltyRequest) => {
        props.payPenalty({
            ...values,
            penaltyNumber: getPenaltyNumber(),
            penaltyAmount: getAmount()
        });
    }

    return (
        <ContentLayout titleTranslateId='penaltyPay.title'>
            <Row className="penalty-pay">
                <Col xl={12}>
                    <Formik validationSchema={getPenaltyValidationSchema}
                        validateOnBlur
                        initialValues={initialGetPenaltyRequest}
                        enableReinitialize
                        onSubmit={(values) => handleGetPenaltyFormSubmit(values)}>
                        {({ handleChange, handleSubmit, values, errors, touched }) => (
                            <Row className="penalty-lookup-row">
                                <Col lg={8} md={7}>
                                    <FormGroup>
                                        <Label>{I18n.t('penaltyPay.penaltyNumberLabel')}</Label>
                                        <Input type="text" name="referenceNumber" onChange={handleChange} placeholder={I18n.t('penaltyPay.penaltyNumber')}
                                            className={errors.referenceNumber && touched.referenceNumber ? ' is-invalid' : ''} value={values.referenceNumber} />
                                        <ErrorMessage name="referenceNumber" component="div" className="invalid-feedback" />
                                    </FormGroup>
                                </Col>
                                <Col lg={4} md={5}>
                                    <Button type="button" color="primary" className="penalty-lookup-button" onClick={handleSubmit}>{I18n.t('penaltyPay.getPenalty')}</Button>
                                </Col>
                            </Row>
                        )}
                    </Formik>

                    {
                        penalty &&
                        <>
                            <HorizontalDivider />

                            <h3>{I18n.t('penaltyPay.penaltyDetailsTitle')}</h3>
                            <div className="penalty-details-card">
                                <div className="penalty-detail-main">
                                    <span className="penalty-detail-label">{I18n.t('penaltyPay.amount')}</span>
                                    <span className="penalty-detail-amount">{`${NumberUtility.format(getAmount() || 0, 2, true, false, ',')} ${primaryCurrency.sign}`}</span>
                                </div>

                                <div className="penalty-detail-grid">
                                    <div className="penalty-detail-item">
                                        <span className="penalty-detail-label">{I18n.t('penaltyPay.penaltyNumber')}</span>
                                        <span>{getPenaltyNumber()}</span>
                                    </div>
                                    {
                                        getIssuedAt() &&
                                        <div className="penalty-detail-item">
                                            <span className="penalty-detail-label">{I18n.t('penaltyPay.issuedAt')}</span>
                                            <span>{DateUtility.formatWithTime(getIssuedAt(), 'dd/mm/yyyy HH:MM')}</span>
                                        </div>
                                    }
                                    {
                                        getStopName() &&
                                        <div className="penalty-detail-item">
                                            <span className="penalty-detail-label">{I18n.t('penaltyPay.stopName')}</span>
                                            <span>{getStopName()}</span>
                                        </div>
                                    }
                                    {
                                        getRouteName() &&
                                        <div className="penalty-detail-item">
                                            <span className="penalty-detail-label">{I18n.t('penaltyPay.routeName')}</span>
                                            <span>{getRouteName()}</span>
                                        </div>
                                    }
                                </div>
                            </div>

                            <Formik validationSchema={penaltyPayValidationSchema}
                                validateOnBlur
                                initialValues={initialPayPenaltyRequest}
                                enableReinitialize
                                onSubmit={(values) => handlePayPenaltyFormSubmit(values)}>
                                {({ handleChange, handleSubmit, values, errors, touched }) => (
                                    <div className="penalty-payment-card">
                                        <FormGroup>
                                            <Label>{I18n.t('penaltyPay.emailLabel')}</Label>
                                            <Input type="text" name="email" onChange={handleChange} placeholder={I18n.t('penaltyPay.email')}
                                                className={errors.email && touched.email ? ' is-invalid' : ''} value={values.email} />
                                            <ErrorMessage name="email" component="div" className="invalid-feedback" />
                                        </FormGroup>

                                        <FormGroup className='mt-2'>
                                            <input type="checkbox" name="areTermsOfUseAccepted" onChange={handleChange}
                                                className={'mr-2' + (errors.areTermsOfUseAccepted && touched.areTermsOfUseAccepted ? ' is-invalid' : '')} checked={values.areTermsOfUseAccepted} />
                                            <span>{I18n.t('penaltyPay.termsOfUseLabel')}</span> &nbsp;
                                            <span className="text-decoration-underline cursor-pointer" onClick={termsOfUseModalOpen}>{I18n.t('findOutMore')}</span>
                                            <ErrorMessage name="areTermsOfUseAccepted" component="div" className="invalid-feedback" />
                                        </FormGroup>

                                        <Button type="button" color="primary" onClick={handleSubmit}>{I18n.t('penaltyPay.pay')}</Button>
                                    </div>
                                )}
                            </Formik>
                        </>
                    }
                </Col>
            </Row>

            <PrometModal
                isOpen={isTermsOfUseModalOpen}
                onOkClick={termsOfUseModalClose}
                onClose={termsOfUseModalClose}
                footer
                headerText={I18n.t('termsOfUse.title')}
                okText={I18n.t('close')}
                size='lg'
            >
                <TermsOfUseContent height='400px' />
            </PrometModal>
        </ContentLayout>
    )
}

const mapDispatchToProps = {
    clearPenalty: clearPenaltyAction,
    getPenalty: getPenaltyActions.request,
    payPenalty: payPenaltyActions.request
};

export default connect(null, mapDispatchToProps)(PenaltyPay as any);
